/* eslint-disable W014 */
/* eslint-disable no-nested-ternary */
/* eslint-disable no-case-declarations */
import semverGte from 'semver/functions/gte';
import postRobot from 'post-robot';

import { fetchServers, serverStateFromApi } from './myServers/fetch';
import { stopGraphqlClient } from './myServers/client';
import i18n from '../i18n';
import {
  KeyServerWretch, WebguiInstallKey, WebguiUpdate, WebguiState, WanIp, WebguiUpdateDns, WebguiUnraidApiCommand,
} from '../api';
import { KeyServer } from '../services';
import { log } from '../log';

import {
  OPEN_POPUP, CAN_POST_MESSAGE, SAFE_JSON_PARSE, GUID_NOT_RECEIVED, WEBGUI_PARENT,
} from '../consts';
import {
  NODE_ENV, REGWIZ_URL, PURCHASE_URL, ACCOUNT_URL,
} from '../env';

let deliverNewStateRetryCounter = 0; // Set outside of receivePostMessage so that it's not set as 0 each time we receive a post message…defeating it's purpose.
const deliverNewStateRetryLimit = 6;
/**
 * @param {string} csrf // required for POSTs in the webgui. feed it `state.csrf`
 */
const updateDns = (csrf) => {
  // we need to run this when PLG is installed to ensure URLs will work in the dashboard after sign in
  setTimeout(() => {
    WebguiUpdateDns
      .formUrl({ csrf_token: csrf })
      .post()
      .res(updateDnsRes => log.debug('[WebguiUpdateDns] %o', updateDnsRes))
      .catch(error => log.error('[WebguiUpdateDns] %o', error));
  }, 500);
};

/**
 *
 */
const preventClose = (e) => {
  e.preventDefault();
  // eslint-disable-next-line no-param-reassign
  e.returnValue = '';
  // eslint-disable-next-line no-alert
  alert(i18n.tc('regWizPopUp.closingPopUpMayLeadToErrors'));
};

/**
 * Previously state.popUpWindow was used.
 * But this eventually lead to a Vue / Vuex error of cross-origin object on the pop-up window.
 * Storing to this variable "fixed" the issue.
 */
let popUpWindow = null;

// eslint-disable-next-line import/prefer-default-export
export const actions = {
  /**
   * @module USERPROFILE
   * @method: openPopUp
   * @description: Open pop-up window or if one already exists we focus on it
   * @param: {string} payload.url
   * @param: {string} payload.name
   * @param: {string} payload.width
   * @param: {string} payload.height
   */
  openPopUp({ commit, dispatch, state }, payload) {
    log.debug('[openPopUp] %o', payload || 'focus existing pop-up');
    if (popUpWindow) return popUpWindow.focus(); // re-focus the existing pop-up window
    /**
     * @description Pop-up blocker detection
     * @see https://stackoverflow.com/a/27725432/3215809
     */
    try {
      // otherwise create a new one
      popUpWindow = OPEN_POPUP(payload.url, payload.name, payload.width, payload.height);
      const isDarkMode = (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
      const isPurchase = payload.url.toString().includes('/preflight');
      const black = '#222';
      const white = '#fff';
      const orangeGradient = 'linear-gradient(to right, #E22828 0%, #ff8c2f 100%)';
      const lightModeBg = (isPurchase ? white : orangeGradient);
      popUpWindow.document.body.style.background = isDarkMode ? black : lightModeBg;
      popUpWindow.focus();
      // in pop-up blocker detection so vuex doesn't update until we know they will be a pop-up
      // after this the popUp is referenced & used via `popUpWindow` instead of `popUpWindow`
      commit('CHANGE_POP_UP_WINDOW', popUpWindow);
      commit('STATE_AT_OPEN', state.registered);
      /**
       * @description every 250ms
       * - detect if the pop up is closed
       * - sending server session postMessage
       */
      const popupClosed = setInterval(() => {
        if (!popUpWindow) clearInterval(popupClosed); // pop-up was close somewhere else so we need to stop checking
        if (popUpWindow && popUpWindow.closed) {
          clearInterval(popupClosed);
          dispatch('resetPopUp', { reloadWebGUI: false });
        }
      }, 250);
      // Detect closure of window that triggered pop-up
      window.onunload = () => {
        if (popUpWindow && !popUpWindow.closed) popUpWindow.close();
      };
      return true;
    } catch (e) {
      // eslint-disable-next-line no-alert
      return alert('Pop-up blocker detected.\nTo continue successfully please add this website to your browser\'s exeception list then refresh.');
    }
  },
  /**
   * @module REGWIZ,USERPROFILE
   * @method: resetPopUp
   * @description: clears cookies related to allowing purchasing. Resets state related to popUpWindow. Refreshes the iframe.
   * @param: {boolean} payload.reloadWebGUI
   */
  resetPopUp({ commit, dispatch }, payload = { reloadWebGUI: true }) {
    log.debug('[resetPopUp] %o', payload);
    popUpWindow = null;
    commit('CHANGE_POP_UP_WINDOW', null);
    dispatch('updateServerState'); // update UPC based on any potential changes that happened during the pop-up's lifespace
    if (payload.reloadWebGUI) dispatch('reloadWebGUI');
  },
  /**
   * @module REGWIZ,USERPROFILE
   * @method: closePopUp
   * @description: Close pop-up window attached to popUpWindow
   * @param: {boolean} payload.reloadWebGUI
   * ```
   */
  closePopUp({ dispatch }, payload = { reloadWebGUI: false }) {
    log.debug('[closePopUp] %o', payload);
    // check if we need to close any open pop-up window
    if (popUpWindow) {
      popUpWindow.close();
      dispatch('resetPopUp', { reloadWebGUI: payload.reloadWebGUI });
    }
  },
  /**
   * @module REGWIZ,USERPROFILE
   * @name reloadWebGUI
   * @description
   */
  reloadWebGUI() {
    log.debug('[reloadWebGUI] %s', '✨');
    window.location.reload();
  },
  /**
   * @module REGWIZ
   * @method closePopUpFromPopUp
   * @description We have a timer setup in the launchpad to autorefresh when it detects that a popup is closed. So explicitly close the popup just in case.
   */
  closePopUpFromPopUp({ dispatch }) {
    log.debug('[closePopUpFromPopUp] %o');
    dispatch('resetPopUp');
    window.close();
  },
  /**
   * @module REGWIZ
   * @name sendDataPostMessageToServer
   * @description
   * @param {object} getters - vuex getters / computed state https://vuex.vuejs.org/guide/getters.html
   */
  sendDataPostMessageToServer({ state }) {
    const payload = (state.submissionFrom === 'SignOut') ? { event: 'MYSERVERS_UNREGISTER' }
      : {
        event: 'REG_WIZARD',
        keyfile: state.keyfile,
        license: state.license,
        email: state.userEmail,
        apikey: state.apikey,
        avatar: state.avatar,
        username: state.username,
        guid: state.guid, // using for `regWizTime` in plg
      };
    log.debug('[sendDataPostMessageToServer] %o', payload);
    try {
      /**
      * if targetOrigin = `state.site` instead of '*' postMessages fail when using Firefox via Unraid OS GUI.
      * We check origins before allowing the postMessage in receivePostMessage this _should_ be fine.
      * We also disallow this code from being in iframes to help prevent postMessage exploits.
      */
      return WEBGUI_PARENT.postMessage(JSON.stringify(payload), state.osGui ? '*' : state.site);
    } catch (error) {
      if (!CAN_POST_MESSAGE) log.warn('[sendDataPostMessageToServer] !CAN_POST_MESSAGE', payload);
      return log.error('[sendDataPostMessageToServer] %o', error);
    }
  },
  /**
   * @REGWIZ
   * @method requestUpdatedServerStateFromUpc
   * @description used by Success page to request new server state from UPC / webgui
   */
  requestUpdatedServerStateFromUpc({ state }) {
    log.debug('[requestUpdatedServerStateFromUpc]');
    try {
      /**
      * if targetOrigin = `state.site` instead of '*' postMessages fail when using Firefox via Unraid OS GUI.
      * We check origins before allowing the postMessage in receivePostMessage this _should_ be fine.
      * We also disallow this code from being in iframes to help prevent postMessage exploits.
      */
      return WEBGUI_PARENT.postMessage(JSON.stringify({ event: 'SUCCESS_NEW_STATE' }), state.osGui ? '*' : state.site);
    } catch (error) {
      if (!CAN_POST_MESSAGE) log.warn('[requestUpdatedServerStateFromUpc] !CAN_POST_MESSAGE', CAN_POST_MESSAGE);
      return log.error('[requestUpdatedServerStateFromUpc] %o', error);
    }
  },
  /**
   * @module USERPROFILE
   * @method: receivePostMessage
   * @description: handles postMessages to the app
   * @param: {Object} payload
   * @param: {EventObject} payload.event - postMessage event. See mdn: https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage#The_dispatched_event
   * @param: {String} payload.event.data - MUST BE STRING AS WE ARE EXPECTING TO PARSE STRING TO JSON
   */
  receivePostMessage({
    commit, dispatch, getters, state,
  }, payload = { event: null }) {
    if (!payload.event) {
      log.error('postMessage no event');
      return;
    }
    log.debug('[receivePostMessage] payload.event %o', payload.event);
    const [err, data] = SAFE_JSON_PARSE(payload.event.data);
    if (err) return;
    if (payload.event.data.includes('post_robot')) {
      log.debug('IGNORE POST ROBOT MESSAGE');
      return;
    }
    if (NODE_ENV === 'development') log.debug('[receivePostMessage] payload.event.data.event %o', data);
    else log.log('[receivePostMessage] payload.event.data.event %o', data.event);
    // these events are allowed the skip the origin check. Used in cases where we block postMessage but still want troubleshooting data.
    const eventsAllowedToPassOriginCheck = ['REQUEST_API_REPORT', 'API_EXECUTE_PINGBACK'];
    const acceptDebugEvents = data && data.event && eventsAllowedToPassOriginCheck.includes(data.event);
    // check origin
    const payloadOrigin = payload.event.origin;
    /**
     * Certain extensions fire a postMessage on initial load of a page.
     * Which ultimately interferes with our communication because they use a similar structure for sending data.
     * So what we can do is attempt to ignore the postMessage and its data so further postMessages from us can come through.
     */
    if (payloadOrigin.includes('extension://')) {
      log.log('Skipping postMessage from browser extension', payloadOrigin);
      return;
    }
    // let's keep going to see if this postMessage is allowed
    const fromRegWiz = payloadOrigin === REGWIZ_URL;
    const fromPurchase = PURCHASE_URL.includes(payloadOrigin); // since PURCHASE_URL includes '/preflight'
    const fromAccount = payloadOrigin === ACCOUNT_URL;
    const fromPreviewUrl = payloadOrigin.includes('d1eohvtyc6gnee.amplifyapp.com'); // pull request preview URLs include this cname
    /**
     * Possible that this is the SESSION event being received so we don't have `state.site` yet.
     * If so use the postMessage's `data.server.site` to compare origin sending message.
     * Additional we'll check against extraOrigins which is set in /boot/config/plugins/dynamix.my.servers/data/extra-origins.json
     */
    const pmOriginMatchesDataServerUrl = data && data.server && data.server.site ? (payloadOrigin === data.server.site) : false;
    const pmOriginMatchesStateSite = (payloadOrigin === state.site);
    const pmOriginIncludedInExtraOrigins = data && data.server && data.server.extraOrigins ? data.server.extraOrigins.includes(payloadOrigin) : false;
    const fromWebGui = data.event === 'SESSION' ? (pmOriginMatchesDataServerUrl || pmOriginIncludedInExtraOrigins) : pmOriginMatchesStateSite;
    const originAllowed = acceptDebugEvents || fromRegWiz || fromPurchase || fromWebGui || fromAccount || fromPreviewUrl;
    /**
      * Capture postMessage not allowed
      * App.vue watches postMessageError – error message for front-end then redirects to Troubleshoot for user to send debugInfo
      * We're capturing data.server in SET_RAW_SERVER_STATE mutation so Troubleshoot can share server state info in email message
      */
    const debugInfo = {
      'data.server.extraOrigins': data && data.server ? data.server.extraOrigins : '',
      'data.server.site': data && data.server ? data.server.site : '',
      'payload.event.origin': payloadOrigin,
      'state.site': state.site,
      pmOriginMatchesDataServerUrl,
      pmOriginMatchesStateSite,
      pmOriginIncludedInExtraOrigins,
      fromRegWiz,
      fromPurchase,
      fromWebGui,
      fromAccount,
      acceptDebugEvents,
      originAllowed,
    };
    if (!originAllowed) {
      log.error('[receivePostMessage] ❌ postMessage origin not allowed ❌ %o', debugInfo);
      commit('SET_POST_MESSAGE_ERROR', debugInfo);
      commit('SET_RAW_SERVER_STATE', data.server); // save data to use for troubleshooting
      return;
    }
    log.debug('[receivePostMessage] originAllowed %o', debugInfo);
    /**
     * sendBackPostMessage
     * @param {object} message
     */
    const sendBackPostMessage = (message) => {
      try {
        log.log('[receivePostMessage.sendBackPostMessage] %o', NODE_ENV === 'development' ? message : message.event);
        /**
        * if targetOrigin = payloadOrigin instead of '*' postMessages fail when using Firefox via Unraid OS GUI.
        * We check origins before allowing the postMessage in receivePostMessage this _should_ be fine.
        * We also disallow this code from being in iframes to help prevent postMessage exploits.
        */
        return payload.event.source.postMessage(JSON.stringify(message), state.osGui ? '*' : payloadOrigin);
      } catch (error) {
        return log.error('[sendBackPostMessage.catch] %o', { error, message });
      }
    };
    /**
     * Handles key install for webgui
     */
    const installKey = async (keyData) => {
      if (!keyData) return log.error('[installKey] missing license data: %s', keyData);
      commit('SET_KEY_INSTALLING', true);
      /** check for duplicate attempt at install of key that matches current server state.
       * When detected same state this may indicate someone refreshed the pop-up while on the success page. */
      let keyType = keyData.split('/'); // parse out key type – make into array, get last item in array, remove '.key', uppercase to match ENUM of state
      keyType = keyType[keyType.length - 1].slice(0, -4).toUpperCase();
      if (keyType === state.state) {
        commit('SET_KEY_INSTALL_FAIL', true);
        sendBackPostMessage({
          event: 'LICENSE_PINGBACK',
          message: 'Failed to license new key – duplicate install detected',
          license: keyData,
          success: false,
        });
        return log.error('[installKey] duplicate install detected', {
          state: state.state,
          keyType,
        });
      }
      log.debug('[receivePostMessage.installKey] %o', keyData);
      const installReponse = await WebguiInstallKey
        .query({ url: keyData })
        .get()
        .res((res) => {
          log.debug('[receivePostMessage.installKey] SUCCESS %o', res);
          setTimeout(() => { // give more time for key install to finish
            /**
             * used here for key installs as UpdateDNS.php requires a key
             * when not installing a key `updateDns` is executed 🤺 in 'REG_WIZARD' handler
             * setTimeout used to avoid hitting "Registration key required" when POSTing
             */
            updateDns(state.csrf);
          }, 1000);
          sendBackPostMessage({
            event: 'LICENSE_PINGBACK',
            message: 'License key installed',
            license: keyData,
            success: true,
          });
          commit('SET_PIRATE_KEY', false); // after a key install we know they're not pirating as the key came from us
        })
        .catch((error) => {
          log.error(`[receivePostMessage.installKey] FAILED: ${keyData}`, error);
          sendBackPostMessage({
            event: 'LICENSE_PINGBACK',
            message: 'Failed to install license key',
            license: keyData,
            success: false,
          });
        });
      return installReponse;
    };
    /**
     * Signs user out of Unraid.net – resets the UPC
     */
    const signOutUnraidNet = async () => {
      log.debug('[receivePostMessage.signOutUnraidNet] 👋');
      const response = await WebguiUpdate
        .formUrl({
          csrf_token: state.csrf,
          '#file': getters.myServersCfgPath,
          '#section': 'remote',
          apikey: '',
          avatar: '',
          email: '',
          username: '',
          wanaccess: 'no',
          accesstoken: '',
          idtoken: '',
          refreshtoken: '',
          ...(!state.hideMyServers && { '#command': '/plugins/dynamix.my.servers/scripts/.nginx_reload' }), // reloads nginx to disable remote access
        })
        .post()
        .res((res) => {
          log.debug('[receivePostMessage.signOutUnraidNet] WebguiUpdate.res %o', res);
          commit('SET_SIGN_OUT_TRIGGERED', true);
          sendBackPostMessage({ // send a ping back to the regwiz
            event: 'ACCOUNT_PINGBACK',
            success: true,
            type: 'signOut',
            webGuiPathname: window.location.pathname,
          });
        })
        .catch((error) => {
          log.error(`[receivePostMessage.signOutUnraidNet] WebguiUpdate.catch Sign out ${state.servername} from Unraid.net failed 😢 %o`, error);
          sendBackPostMessage({ // send a ping back to the regwiz
            event: 'ACCOUNT_PINGBACK',
            success: false,
            type: 'signOut',
            webGuiPathname: window.location.pathname,
            message: 'Sign Out request failed',
          });
        });
      return response;
    };

    /**
     * @note
     * if we check origin here like `if (!ALLOWED_ORIGINS.includes(payloadOrigin)) return`
     * then this would potentially block our pop-up window from receiving a ping back response from the webGUI.
     * This is why we aren't checking postmessage event origin here
     */
    switch (data.event) {
    case 'SESSION': // received by pop-up
      log.debug('[receivePostMessage] SESSION');
      commit('SET_FULL_SERVER_STATE', data.server); // why both SET_FULL_SERVER_STATE & SET_RAW_SERVER_STATE? See comment in mutations.js for SET_RAW_SERVER_STATE
      commit('SET_RAW_SERVER_STATE', data.server); // save data that won't get mutated for troubleshooting
      i18n.mergeLocaleMessage(data.locale, data.messages); // add messages into i18n
      i18n.locale = data.locale; // set locale to received locale
      log.debug(`[receivePostMessage] SESSION ${i18n.locale} locale & messages set`, i18n.messages);
      break;
    case 'PREFLIGHT_REQUEST': // received by upc
      log.debug('[receivePostMessage] PREFLIGHT_REQUEST');
      if (!getters.allowedOrigins.includes(payloadOrigin)) { // ensure the requester is allowed, otherwise we say, NO NO!
        log.error('[🙅‍♂️ ACCESS DENIED]');
        break;
      }
      /**
       * @note - if you update anything here please double check both places that receives
       * this postMessage back to the sender of 'PREFLIGHT_REQUEST':
       * - swtich `case 'SESSION':` where we set `data.server` & `data.messages`
       * - @unraid/craft-unraid * `web/helpers/preflight.js`
       */
      try {
        dispatch('updateServerState')
          .then((res) => {
            log.debug('[receivePostMessage] PREFLIGHT_REQUEST.updateServerState.then %o', { res, 'getters.server': getters.server });
            sendBackPostMessage({
              event: 'SESSION',
              server: {
                ...getters.server,
              },
              // this feeds the existing locale data from UPC to the pop-up
              locale: i18n.locale,
              messages: {
                ...i18n.messages[i18n.locale],
              },
            });
          })
          .catch((error) => {
            log.error('[receivePostMessage] PREFLIGHT_REQUEST.updateServerState.catch %o', error);
            sendBackPostMessage({ event: 'DELIVER_NEW_STATE', success: false });
          });
      } catch (catchError) {
        log.error('[receivePostMessage] PREFLIGHT_REQUEST.updateServerState.trycatch %o', catchError);
        sendBackPostMessage({ event: 'DELIVER_NEW_STATE', success: false });
      }
      break;
    case 'LICENSE_PINGBACK': // received by pop-up
      log.debug('[receivePostMessage] LICENSE_PINGBACK');
      commit('LICENSE_PINGBACK', data);
      break;
    case 'ACCOUNT_PINGBACK': // received by pop-up
      log.debug('[receivePostMessage] ACCOUNT_PINGBACK');
      commit('ACCOUNT_PINGBACK', data);
      break;
    case 'DELIVER_NEW_STATE': // received by pop-up
      log.debug('[receivePostMessage] DELIVER_NEW_STATE %o', data);
      /**
       * If we've installed a key we need to check if the data received from the postmessage is "new" rather than stale / out-dated.
       * To do this we can check `state.state` and `data.state` (the data received in the postmessage). If they match we need to ask again.
       * This is the result of installing a key and very intermittently receiving the same server state before the key install.
       */
      const keyToInstall = !!(state.license);
      const failedKeyInstall = state.licenseAction && !state.licenseAction.success;
      const oldState = state.state;
      const newState = (data && data.state) ? data.state : null;
      const potentiallyStaleState = oldState === newState;
      log.debug('[receivePostMessage] DELIVER_NEW_STATE %o', {
        keyToInstall, failedKeyInstall, oldState, newState, potentiallyStaleState, deliverNewStateRetryCounter,
      });
      if (failedKeyInstall) { // we know the key installed failed so we don't really need the server state to be updated in the pop-up. Just the user to close and manually install the key.
        commit('RECEIVED_SUCCESS_NEW_SERVERSTATE', Date.now()); // this value is watched in Success.vue to set loading = false
        // commit('SET_APP_ERROR_MESSAGE', 'Detected failed key install. Please follow the manual install instructions.');
        break;
      }
      if (keyToInstall && potentiallyStaleState && deliverNewStateRetryCounter < deliverNewStateRetryLimit) { // send another postMessage from pop-up to UPC to ask for new state. Hopefully a non-stale one.
        setTimeout(() => { // give more time for key install to finish and server to update its state
          log.debug('[potentiallyStaleState] %o', { potentiallyStaleState });
          log.debug('[deliverNewStateRetryCounter] %o', { deliverNewStateRetryCounter });
          dispatch('requestUpdatedServerStateFromUpc');
          deliverNewStateRetryCounter += 1;
        }, 350 * deliverNewStateRetryCounter); // slight back off each retry
        break;
      } else if (keyToInstall && potentiallyStaleState && deliverNewStateRetryCounter >= deliverNewStateRetryLimit) { // hit retry limit or somehow got above it…
        /** set error for success page to mention disconnect between upc & pop-up */
        commit('SET_APP_ERROR_MESSAGE', `Failed to receive latest server info${state.licenseAction && state.licenseAction.success ? ' after successful key install' : ''}. Please close this pop-up and refresh the webGUI.`);
        commit('RECEIVED_SUCCESS_NEW_SERVERSTATE', Date.now()); // this value is watched in Success.vue to set loading = false
        break;
      }
      // if we reach here we assume the data received is fresh
      commit('SET_FULL_SERVER_STATE', data); // why both SET_FULL_SERVER_STATE & SET_RAW_SERVER_STATE? See comment in mutations.js for SET_RAW_SERVER_STATE
      commit('SET_RAW_SERVER_STATE', data); // save data that won't get mutated for troubleshooting
      commit('RECEIVED_SUCCESS_NEW_SERVERSTATE', Date.now()); // this value is watched in Success.vue to set loading = false
      deliverNewStateRetryCounter = 0; // reset counter once we receive non-stale state 🤝
      break;
    // these postMessage events we're originally handled in the webgui PLG
    case 'KEY_PURCHASE': // received by upc
      log.debug('[receivePostMessage] KEY_PURCHASE');
      installKey(data.license);
      if (state.processStarted) commit('SET_PROCESS_STARTED', null);
      if (data.oem) signOutUnraidNet(); // automatically sign out the OEM purchaser from the UPC so they don't need to manually sign out before shipping server to customer
      break;
    case 'MYSERVERS_UNREGISTER': // received by upc
      log.debug('[receivePostMessage] MYSERVERS_UNREGISTER WebguiUpdate');
      if (state.processStarted) commit('SET_PROCESS_STARTED', null);
      // Extract JWT Tokens and save them. Note camel casing here and it not being used below.
      commit('SET_JWTS', null); // reseting any saved JWTs
      signOutUnraidNet();
      break;
    case 'REG_WIZARD': // received by upc
      log.debug('[receivePostMessage] REG_WIZARD %o', data);
      // this.commit('SET_SHOW_POST_MESSAGE_STATUS', true);
      if (data.license && (getters.stateData.error || !state.keyfile)) { // only attempt to install a key if we need to
        // duplicate conditional so we don't get the error from installKey() b/c not every instance of this event will have license
        installKey(data.license);
      }
      log.debug('[receivePostMessage] REG_WIZARD WebguiUpdate %o', data);

      // Registration finished, reset the value
      if (state.processStarted) commit('SET_PROCESS_STARTED', null);

      // Extract JWT Tokens and save them. Note camel casing here and it not being used below.
      commit('SET_JWTS', {
        ...(data.accessToken && { accessToken: data.accessToken }),
        ...(data.idToken && { idToken: data.idToken }),
        ...(data.refreshToken && { refreshToken: data.refreshToken }),
      });

      WebguiUpdate
        .formUrl({
          csrf_token: state.csrf,
          '#file': getters.myServersCfgPath,
          '#section': 'remote',
          regWizTime: `${Date.now()}_${data.guid}`, // set when signing in the first time and never unset for the sake of displaying Sign In/Up in the UPC without needing to validate guid every time
          // conditionally spread
          ...((data.apikey) && { apikey: data.apikey }), // only add apikey when we have the plg installed
          ...(data.email && { email: data.email }),
          ...(data.username && { username: data.username }),
          ...(data.avatar && { avatar: data.avatar }),
          // note that the object keys here are all lowercase. Uraid API requires this.
          ...(data.accessToken && { accesstoken: data.accessToken }),
          ...(data.idToken && { idtoken: data.idToken }),
          ...(data.refreshToken && { refreshtoken: data.refreshToken }),
          // '#command': '/webGui/include/UpdateDNS.php',
        })
        .post()
        .res((res) => {
          log.debug('[receivePostMessage] REG_WIZARD WebguiUpdate.res %o', res);
          if (!data.license) updateDns(state.csrf); // used here for key installs as UpdateDNS.php requires a key
          commit('SET_SIGN_OUT_TRIGGERED', false);
          let countIntervalRuns = 0;
          const intervalForUpdatedState = setInterval(() => { // keep asking webgui for latest server state after sign in. Fixes: https://app.asana.com/0/inbox/1159909068082281/1202170924026637/1202171128984683
            log.debug('[receivePostMessage] REG_WIZARD WebguiUpdate.res intervalForUpdatedState');
            countIntervalRuns += 1; // increment the counter
            try {
              dispatch('updateServerState')
                .then((updatedState) => {
                  log.debug('[receivePostMessage] REG_WIZARD WebguiUpdate.res intervalForUpdatedState updatedState %o', { updatedState, countIntervalRuns });
                  if (
                    (!state.hideMyServers
                      && (
                        (!state.registered && countIntervalRuns >= 4) // if not registered let it run 4 times
                        || (state.registered && updatedState.username && updatedState.username !== 'root') // if registered ensure we finally received the username, tell the pop-up to proceed
                      )
                    )
                    || (state.hideMyServers && countIntervalRuns >= 4) // if we don't have plugin installed let this run a few times
                  ) {
                    clearInterval(intervalForUpdatedState); // stop interval from running
                    sendBackPostMessage({ // send a ping back to the regwiz
                      event: 'ACCOUNT_PINGBACK',
                      success: true,
                      type: 'signIn',
                      webGuiPathname: window.location.pathname,
                      res: updatedState,
                    });
                  }
                })
                .catch((error) => {
                  log.error('[receivePostMessage] REG_WIZARD WebguiUpdate.re intervalForUpdatedState error %o', { error, countIntervalRuns });
                  clearInterval(intervalForUpdatedState); // stop interval from running
                  sendBackPostMessage({ // send a ping back to the regwiz
                    event: 'ACCOUNT_PINGBACK',
                    success: false,
                    type: 'signIn',
                    webGuiPathname: window.location.pathname,
                    message: 'Failed to receive updated server details',
                  });
                });
            } catch (catchError) {
              log.error('[receivePostMessage] REG_WIZARD WebguiUpdate catchError %o', { catchError, countIntervalRuns });
              clearInterval(intervalForUpdatedState); // stop interval from running
              sendBackPostMessage({ // send a ping back to the regwiz
                event: 'ACCOUNT_PINGBACK',
                success: false,
                type: 'signIn',
                webGuiPathname: window.location.pathname,
                message: 'Failed to receive updated server details',
              });
            }
          }, 250);
        })
        .catch((error) => {
          log.error('[WebguiUpdate] %o', error);
          sendBackPostMessage({ // send a ping back to the regwiz
            event: 'ACCOUNT_PINGBACK',
            success: false,
            type: 'signIn',
            webGuiPathname: window.location.pathname,
            message: 'Failed clearing configuration',
          });
        });
      break;
    case 'RELOAD': // received by upc
      log.debug('[receivePostMessage] RELOAD');
      window.location.reload();
      break;
    case 'SUCCESS_NEW_STATE': // received by upc
      log.debug('[receivePostMessage] SUCCESS_NEW_STATE');
      try {
        dispatch('updateServerState')
          .then((res) => {
            log.debug('[receivePostMessage] SUCCESS_NEW_STATE.updateServerState.then');
            sendBackPostMessage({ ...res, event: 'DELIVER_NEW_STATE', success: true });
          })
          .catch((error) => {
            log.error('[receivePostMessage] SUCCESS_NEW_STATE.updateServerState.catch %o', error);
            sendBackPostMessage({ event: 'DELIVER_NEW_STATE', success: false });
          });
      } catch (catchError) {
        log.error('[receivePostMessage] SUCCESS_NEW_STATE.updateServerState.trycatch %o', catchError);
        sendBackPostMessage({ event: 'DELIVER_NEW_STATE', success: false });
      }
      break;
    case 'REDIRECT_MAIN': // received by upc
      log.debug('[receivePostMessage] REDIRECT_MAIN');
      window.location.href = '/Main';
      break;
    // interact with unraid-api in webgui
    case 'REQUEST_API_REPORT': // received by upc
      const param1 = (state.apiVersion && semverGte(state.apiVersion, '2.43.3')) ? '-vv' : '-v';
      log.debug('[receivePostMessage] REQUEST_API_REPORT %o', { apiVersion: state.apiVersion, param1 });
      try {
        // if 2.43 or higher run `unraid-api report -vv` for un anonymized data
        WebguiUnraidApiCommand
          .formUrl({
            csrf_token: state.csrf,
            command: 'report',
            param1,
          })
          .post()
          .json((json) => {
            log.debug('👼 [receivePostMessage] REQUEST_API_REPORT WebguiUnraidApiCommand.json %o', json);
            commit('SET_API_EXECUTE_RESPONSE', json.result);
            sendBackPostMessage({ // send a ping back to the regwiz
              event: 'API_EXECUTE_PINGBACK',
              result: json.result,
            });
          })
          .catch((error) => {
            log.error('[receivePostMessage] REQUEST_API_REPORT WebguiUnraidApiCommand.catch failed to execute `unraid-api` report 😢 %o', error);
            sendBackPostMessage({ // send a ping back to the regwiz
              event: 'API_EXECUTE_PINGBACK',
              error,
            });
          });
      } catch (catchError) {
        log.error('[receivePostMessage] REQUEST_API_REPORT WebguiUnraidApiCommand.catch failed to execute `unraid-api` report 😢 %o', catchError);
        sendBackPostMessage({ // send a ping back to the regwiz
          event: 'API_EXECUTE_PINGBACK',
          error: catchError,
        });
      }
      break;
    case 'API_EXECUTE_PINGBACK': // received by pop-up
      log.debug('[receivePostMessage] API_EXECUTE_PINGBACK');
      if (data && data.result) commit('SET_API_EXECUTE_RESPONSE', data.result);
      break;
    /**
     * Received by UPC to show message for pirate
     * @param {string} data.event
     * @param {string} data.ts
     */
    case 'YARG_MATEY':
      log.debug('[receivePostMessage] YARG_MATEY');
      commit('SET_PIRATE_KEY', data.ts);
      break;
    default: log.debug('[receivePostMessage] UNHANDLED_MESSAGE');
    }
  },
  /**
   * @module USERPROFILE
   * @param {stringify json object} payload – serverstate
   */
  setStateFromWebComponentProp({ commit }, payload) {
    log.debug('[setStateFromWebComponentProp]');
    // decode the encoded prop, then try to parse as JSON
    const [err, data] = SAFE_JSON_PARSE(decodeURIComponent(payload));
    if (err) return log.error('[setStateFromWebComponentProp] %o', payload);
    return commit('SET_FULL_SERVER_STATE', data);
  },
  /**
   * @module USERPROFILE,REGWIZ
   * @note RegWiz uses postMessages to request data from server
   */
  async updateServerState({
    commit, dispatch, getters, state,
  }) {
    log.debug('[updateServerState]');
    if (REGWIZ_URL === window.location.origin) return log.debug('[SKIPPED] updateServerState()'); // prevents an error when in UPC development env
    /** determine what endpoint to hit for state - state.php or the graphql API */
    log.debug('[updateServerState]', {
      'state.signOutTriggered': state.signOutTriggered,
      'state.hideMyServers': state.hideMyServers,
      '!state.graphOnline': !state.graphOnline,
      '!getters.validApiKeyLength': !getters.validApiKeyLength,
      '!getters.validApiKeyForUpc': !getters.validApiKeyForUpc,
    });
    if (state.signOutTriggered || state.hideMyServers || !state.graphOnline || !getters.validApiKeyLength || !getters.validApiKeyForUpc) {
      log.debug('[updateServerState] via ', getters.statePhpPath);
      await WebguiState
        .url(getters.statePhpPath)
        .get()
        .json()
        .then((res) => {
          if (!res) return log.error('[updateUpcFromStatePhp] no `res` skipping `SET_FULL_SERVER_STATE`');
          return commit('SET_FULL_SERVER_STATE', res);
        })
        .catch((error) => {
          /** @idea - potentially do `error.toString()` to prevent issues down the line
           * Unexpected token === server is running but CSRF token is wrong or something else is causing a redirect to home
           * 503 rate limited. stop polling.
          */
          log.error('[updateUpcFromStatePhp WebguiState.catch] %o', error);
          if (error.toString().includes('Unexpected token') || error.toString().includes('503')) { // Session is invalid, user has logged out from another tab
            window.location.href = '/'; // stop all polling, do NOT pass GO, DO NOT COLLECT $200
          } else if (error === 'TypeError: Failed to fetch') { // do nothing, server is probably rebooting
          }
        });
    } else {
      log.debug('[updateServerState] via serverStateFromApi');
      await dispatch('serverStateFromApi');
    }
    const newState = getters.server; // since we await both options this should be up-to-date by the time we get here
    log.debug('[updateServerState] newState %o', newState);
    return newState; // so we can access via promises. probably redundant since we can access getters.servers anyway.
  },
  /**
   * @module LAUNCHPAD,REGWIZ
   * @param {string} guid
   */
  async validateGuid({ commit, getters, state }) {
    await new Promise((resolve, reject) => {
      if (state.guidValidationRunning) return log.debug('[validateGuid] already validating');
      commit('CHANGE_GUID_VALIDATION_RUNNING', true); // help prevent duplicate validations from running
      const regWizTimeGuid = state.regWizTime ? state.regWizTime.split('_')[1] : null;
      const previouslyRegistered = regWizTimeGuid ? (regWizTimeGuid === state.guid) : false;
      log.debug('[validateGuid] %o', {
        guid: state.guid,
        regGuid: state.regGuid,
        regWizTimeGuid,
        previouslyRegistered,
      });
      /**
       * @NOTE – DO NOT do `commit('SET_LOADING_STATE', true);`
       * Unless you want to create an infinite loop in the pop-up Index.vue page
       */
      let errorMessage = 'GUID Invalid';
      if (!state.guid || state.guid === GUID_NOT_RECEIVED) {
        log.debug(`[validateGuid] ${errorMessage} %o`, { '!state.guid': !state.guid, GUID_NOT_RECEIVED });
        errorMessage = `${errorMessage}: not present`;
        commit('CHANGE_GUID_VALID', false);
        commit('CHANGE_REPLACEABLE', false);
        commit('SET_STATE', 'EBLACKLISTED1');
        commit('SET_APP_ERROR_MESSAGE', errorMessage);
        commit('CHANGE_GUID_VALIDATION_RUNNING', false);
        commit('SET_LOADING_STATE', false);
        return resolve(false);
      }
      /**
       * If the server has been registered before see if the GUID in the value saved matches
       * if so DO NOT ping the key server
       * - but we should always ping when stateData.error
       */
      if (previouslyRegistered && !getters.stateData.error) {
        log.debug('[validateGuid] previouslyRegistered %s', state.guid);
        commit('CHANGE_GUID_REGISTERED', true); /** @important must come before `CHANGE_GUID_VALID` */
        commit('CHANGE_GUID_VALID', true);
        commit('CHANGE_GUID_FORUMACCOUNT', true); // assuming true since guid is registered
        commit('CHANGE_GUID_VALIDATION_RUNNING', false);
        commit('SET_LOADING_STATE', false);
        return resolve(true);
      }
      const tweleveZeros = state.guid.includes('000000000000'); // 12 consecutive zeros
      // quick client side validation to prevent unnecessary endpoint POST
      // guid must be present and not equal to `GUID_NOT_RECEIVED`
      // But if there's twelve 0s or not long enough it's invalid so don't send to validation endpoint
      if (state.guid && state.guid !== GUID_NOT_RECEIVED && (tweleveZeros || state.guid.length !== 27)) {
        if (tweleveZeros) {
          errorMessage += ': Too many consecutive zeros';
          commit('SET_STATE', 'EBLACKLISTED2');
        } else {
          errorMessage += ': Not long enough';
          commit('SET_STATE', 'EBLACKLISTED1');
        }
        commit('CHANGE_GUID_VALID', false);
        commit('CHANGE_REPLACEABLE', false);
        commit('SET_APP_ERROR_MESSAGE', errorMessage);
        commit('CHANGE_GUID_VALIDATION_RUNNING', false);
        commit('SET_LOADING_STATE', false);
        return reject(new Error(errorMessage));
      }
      /**
       * ping validation endpoint
       * - when 'EGUID' we need to validate the registered GUID not the current flash GUID.
       * - Otherwise the `replaceable` value that's return will always be `false` since the current flash GUID will theoretically be new.
       */
      if (!state.regGuid) log.warn('[validateGuid] state.regGuid empty. Getting regGuid from state.regWizTime.');
      const regGuid = state.regGuid || regWizTimeGuid || ''; // There's been an instance when EGUID where `state.regGuid` is null but regWizTime contains the previous regGuid…so we try to take advantage of that here with const regGuid
      // if EGUID we need to first validate the regGuid to check eligibility replacement
      // if the regGuid has already been replaced this will result in an error in the pop-up
      commit('CHANGE_GUID_FOR_VALIDATION', state.state === 'EGUID' ? regGuid : state.guid);
      commit('CHANGE_GUID_BLACKLISTED', null);
      try {
        const validate = KeyServerWretch.url('/validate/guid')
          .formUrl({ guid: state.guidForValidation })
          .post()
          .forbidden((err) => {
            let usableErrMsg;
            const [parsedError, parsedData] = SAFE_JSON_PARSE(err.message); // see if we need to parse the string for json
            if ((!parsedData || parsedError) && err.message) usableErrMsg = err.message; // no json use original string
            if (parsedData && parsedData.error) usableErrMsg = parsedData.error; // we got json
            log.error('[validateGuid] POST.forbidden %o', usableErrMsg);
            // if the response error message includes 'blacklist' we know the server is blacklisted
            // we don't want to show blacklisted error for regGUID when in EGUID state as the error message displayed is incorrect.
            if (usableErrMsg.includes('blacklisted')) {
              commit('CHANGE_GUID_BLACKLISTED', true);
              if (state.state !== 'EGUID') {
                commit('SET_STATE', 'EBLACKLISTED');
                commit('SET_APP_ERROR_MESSAGE', usableErrMsg);
              } else log.warn('[validateGuid] skip EBLACKLISTED state overwrite');
            } else if (usableErrMsg.includes('invalid GUID')) { // to ensure we show the correct error state for the user if they dont have a valid GUID or there was no GUID passed
              if (state.state !== 'EGUID') {
                commit('SET_STATE', 'EBLACKLISTED1');
                commit('SET_APP_ERROR_MESSAGE', usableErrMsg);
              } else log.warn('[validateGuid] skip EBLACKLISTED state overwrite');
            }
            commit('CHANGE_GUID_VALID', false);
            commit('CHANGE_REPLACEABLE', false);
          })
          .json((json) => {
            log.debug('[validateGuid] POST.json %o', json);
            if (json.registered) commit('CHANGE_GUID_REGISTERED', json.registered); /** @important must come before `CHANGE_GUID_VALID` */
            commit('CHANGE_GUID_FORUMACCOUNT', json.forumaccount || false);
            commit('CHANGE_GUID_VALID', true); // successful response means the GUID is valid.
            commit('CHANGE_REPLACEABLE', json.replaceable);
          })
          .catch((err) => {
            log.error('[validateGuid] POST.catch %o', err);
            commit('SET_APP_ERROR_MESSAGE', err.message);
            reject(new Error(err));
          })
          .finally(() => {
            log.debug('[validateGuid] POST.finally');
            commit('CHANGE_GUID_VALIDATION_RUNNING', false);
            commit('SET_LOADING_STATE', false);
          });

        return resolve(validate);
      } catch (error) {
        log.error('[validateGuid] try/catch %o', error);
        commit('SET_APP_ERROR_MESSAGE', error);
        commit('CHANGE_GUID_VALIDATION_RUNNING', false);
        return reject(new Error(error));
      }
    });
  },

  /**
   * @module LAUNCHPAD,UPC
   * This action does two POSTs and a GET
   * POST 1. Key Server w/ timestamp in seconds + guid
   * POST 2. After receiving successful response, we install key
   */
  async startTrial({ commit, dispatch, state }) {
    log.debug('[startTrial]');
    commit('SET_LOADING_STATE', 'Fetching trial…');
    window.addEventListener('beforeunload', preventClose);
    // open launchpad for loading state while getting and installing trial
    if (!state.isLaunchpadOpen) commit('SET_IS_LAUNCHPAD_OPEN', false);
    // POST 1. Key Server w/ timestamp + guid
    await KeyServer.startTrial({
      timestamp: Math.floor(new Date().getTime() / 1000), // timestamp in seconds,
      guid: state.guid,
    })
      .json()
      .then((trialRes) => {
        // POST 2. After receiving successful response, we install key
        // we have the trial details now. Save to server
        commit('SET_LOADING_STATE', 'Installing trial…');
        WebguiInstallKey
          .query({ url: trialRes.license })
          .get()
          .res((res) => {
            log.debug('[KeyServer.startTrial.WebguiInstallKey] SUCCESS %o', res);
            setTimeout(() => { // give more time for key install to finish
              commit('SET_LOADING_STATE', 'Verifying trial install…');

              // open unraid.net pop-up window to track trial conversions
              dispatch('openPopUp', state.trialWindow); // open tiny cookie setting window behind the scenes
              if (popUpWindow) popUpWindow.blur(); // go away from new pop-up window
              window.focus(); // re-focus the current webgui window

              setTimeout(() => { // give more time for key install to finish
                commit('SET_LOADING_STATE', 'Reloading webgui…');
                // open the plg promo after installing trial
                sessionStorage.setItem('showPromoPostTrialInstall', Date.now());
                commit('SET_PIRATE_KEY', false); // after a key install we know they're not pirating as the key came from us
                window.removeEventListener('beforeunload', preventClose);
                window.location.reload();
              }, 4000);
            }, 1500);
          })
          .catch((error) => {
            log.error(`[KeyServer.startTrial.WebguiInstallKey.catch] FAILED: ${trialRes}`, error);
            // eslint-disable-next-line no-unused-vars
            const [err, data] = SAFE_JSON_PARSE(error.message);
            commit('SET_APP_ERROR_MESSAGE', data ? (data.errorMessage ? data.errorMessage : data.error) : error);
            commit('SET_LOADING_STATE', false);
          });
      })
      .catch((error) => {
        log.error('[KeyServer.startTrial.catch] FAILED', error);
        // eslint-disable-next-line no-unused-vars
        const [err, data] = SAFE_JSON_PARSE(error.message);
        commit('SET_APP_ERROR_MESSAGE', data ? (data.errorMessage ? data.errorMessage : data.error) : error);
        commit('SET_LOADING_STATE', false);
      });
  },
  /**
   *
   */
  togglePromo({ commit, state }) {
    log.debug('[togglePromo]');
    // do we need to inject the promo before showing it?
    const wcName = 'unraid-promo';
    if (!document.getElementsByTagName(wcName).length) {
      const body = document.getElementsByTagName('body')[0];
      const lp = document.createElement(wcName);
      body.appendChild(lp);
    }
    commit('SET_IS_LAUNCHPAD_OPEN', false);
    commit('SET_IS_PROMO_OPEN', !state.isPromoOpen);
  },
  /**
   *
   */
  jumpToUpc({ commit }) {
    // scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
    // open the UPC
    commit('TRIGGER_UPC', true);
    // reset `state.triggerUPC` after Xms
    setTimeout(() => {
      commit('TRIGGER_UPC', false);
    }, 500);
  },
  async getWanIp({ commit, state }) {
    log.debug('[getWanIp]');
    if (state.wanip) return log.debug('[getWanIp] SKIPPED', state.wanip);
    try {
      return commit('SET_WANIP', await WanIp.get().text());
    } catch (error) {
      return log.error('[getWanIp] Unable to get WAN IP');
    }
  },
  /**
   * Executes an unraid-api command
   * @param {String} payload.command ex: 'start', 'stop'
   * @param {Array} payload.param1 ex: ['-v'],
   */
  async executeUnraidApi({ commit, state }, payload = { command: '', param1: null }) {
    log.debug('[executeUnraidApi]', payload);
    const { command, param1 } = payload;
    // Trigger front-end interactivty based on command
    if (command === 'start') commit('SET_MY_SERVERS_LOADING', true);
    const response = await WebguiUnraidApiCommand
      .formUrl({
        csrf_token: state.csrf,
        command,
        param1,
      })
      .post()
      .json((json) => {
        log.debug('👼 [executeUnraidApi] json %o', json);
      })
      .catch((error) => {
        log.error(`[executeUnraidApi] catch failed to execute unraid-api ${command} 😢 %o`, error);
      });
    return response;
  },

  /**
   * Improved way of listening and responding to post messages instead of handling manually
   * @see https://www.npmjs.com/package/post-robot
   */
  postRobotListeners({ commit, state, getters }) {
    log.debug('[postRobotListeners] listening', ACCOUNT_URL);

    postRobot.on(
      'getServer',
      { window: state.popUpWindow },
      () => {
        log.debug('[postRobotListeners] getServer');
        return getters.server;
      },
    );

    postRobot.on(
      'processStarted',
      { window: state.popUpWindow },
      (event) => {
        log.debug('[postRobotListeners] processStarted', event.data);

        const process = event.data && event.data.process ? event.data.process : null;

        try {
          log.debug('[postRobotListeners] processStarted');
          commit('SET_PROCESS_STARTED', process);
        } catch (error) {
          // eslint-disable-next-line no-alert
          alert('Error setting SET_PROCESS_STARTED');
        }
      },
    );

    // postRobot.on(
    //   'postRegister',
    //   { window: state.popUpWindow },
    //   () => {
    //     log.debug('[postRobotListeners] postRegister');
    //     // Registration finished, reset the value
    //     if (state.processStarted) commit('SET_PROCESS_STARTED', false);
    //     return getters.server;
    //   },
    // );

    postRobot.on(
      'postUnregister',
      { window: state.popUpWindow },
      async () => {
        log.debug('[postRobotListeners] postUnregister');
        commit('SET_PROCESS_STARTED', false);
        const response = await WebguiUpdate
          .formUrl({
            csrf_token: state.csrf,
            '#file': getters.myServersCfgPath,
            '#section': 'remote',
            apikey: '',
            avatar: '',
            email: '',
            username: '',
            wanaccess: 'no',
            ...(!state.hideMyServers && { '#command': '/plugins/dynamix.my.servers/scripts/.nginx_reload' }), // reloads nginx to disable remote access
          })
          .post()
          .res((res) => {
            log.debug('[receivePostMessage.signOutUnraidNet] WebguiUpdate.res %o', res);
            commit('SET_SIGN_OUT_TRIGGERED', true);
          })
          .catch(error => log.error(`[receivePostMessage.signOutUnraidNet] WebguiUpdate.catch Sign out ${state.servername} from Unraid.net failed 😢 %o`, error));
        return response;
      },
    );

    // postRobot.on(
    //   'handleRecoverLicense',
    //   { window: state.popUpWindow },
    //   () => {
    //     log.debug('[postRobotListeners] handleRecoverLicense');
    //     return getters.server;
    //   },
    // );

    // postRobot.on(
    //   'handleReplaceLicense',
    //   { window: state.popUpWindow },
    //   () => {
    //     log.debug('[postRobotListeners] handleReplaceLicense');
    //     return getters.server;
    //   },
    // );
  },

  /**
     * @name installPlugin
     * @param {String} plgType - 'dynamix.unraid.net.plg' or 'dynamix.unraid.net.staging.plg'
     */
  installPlugin({ commit }, payload = { plgType: '', update: false }) {
    try {
      const file = `https://sfo2.digitaloceanspaces.com/unraid-dl/unraid-api/${payload.plgType || 'dynamix.unraid.net.plg'}`;
      log.debug('[installPlugin] %s', file);
      if (!payload.update) {
        sessionStorage.setItem('clickedinstallPlugin', Date.now());
        commit('SET_IS_PROMO_OPEN', false);
      }
      // payload.update ? i18n.tc('upc.updatingMyServers') : i18n.tc('upc.installingMyServers'),
      const modalTitle = payload.update ? 'Updating Connect (beta)' : 'Installing Connect (beta)';
      // eslint-disable-next-line no-undef
      if (typeof openPlugin === 'function') { // `openPlugin` will be included in 6.10.4+ DefaultPageLayout
        log.debug('[installPlugin] using openPlugin %s', file);
        // eslint-disable-next-line no-undef
        openPlugin(
          `plugin ${payload.update ? 'update' : 'install'} ${file}`,
          modalTitle,
          '',
          'refresh',
        );
      } else {
        log.debug('[installPlugin] using openBox %s', file);
        // `openBox()` is defined in the webgui's DefaultPageLayout.php and used when openPlugin is not available
        // eslint-disable-next-line no-undef
        openBox(
          `/plugins/dynamix.plugin.manager/scripts/plugin&arg1=install&arg2=${file}`,
          modalTitle,
          600,
          900,
          true,
        );
      }
    } catch (error) {
      log.error(error);
    }
  },

  fetchServers,
  serverStateFromApi,
  stopGraphqlClient,
};
